import React, { useEffect, useMemo, useState } from "react";
import "./UserProfileModal.css";
import ReviewsModal from "../reviews/ReviewsModal";

const as = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const normalizeLocale = (locale) => {
  const v = String(locale || "").toLowerCase();
  return v === "kk" || v === "kz" ? "kk" : "ru";
};

export default function UserProfileModal({
  user: baseUser,
  userDetails = null,
  locale = "kk",

  canEdit = false,
  canDelete = false,

  onEdit,
  onDelete,

  infoLoading = false,
  reviewsLoading = false,
  onInfoTabOpen,
  onReviewsTabOpen,

  reviewsResp = null,
  myReview = null,
  canWriteReview = true,
  canManageReview = false,
  onReviewsPageChange,
  onReviewSave,
  onReviewDeleteMine,
  onReviewToggleVisibility,
}) {
  const [tab, setTab] = useState("info");
  const lang = normalizeLocale(locale);

  const t = useMemo(() => {
    const kk = lang === "kk";

    return {
      title: kk ? "Қолданушы профилі" : "Профиль пользователя",
      info: kk ? "Ақпарат" : "Информация",
      reviews: kk ? "Пікірлер" : "Отзывы",
      contact: kk ? "Байланыс" : "Контакты",
      work: kk ? "Жұмыс" : "Работа",
      docs: kk ? "Құжаттар" : "Документы",
      role: kk ? "Рөл" : "Роль",
      roles: kk ? "Рөлдер" : "Роли",
      dept: kk ? "Бөлім" : "Отдел",
      subject: kk ? "Пән" : "Предмет",
      status: kk ? "Мәртебе" : "Статус",
      phone: kk ? "Телефон" : "Телефон",
      address: kk ? "Мекенжай" : "Адрес",
      position: kk ? "Қызметі" : "Должность",
      degree: kk ? "Ғылыми дәрежесі" : "Ученая степень",
      createdAt: kk ? "Құрылған күні" : "Дата создания",
      updatedAt: kk ? "Жаңартылған күні" : "Дата обновления",
      iin: "IIN",
      iik: "IIK",
      docNo: kk ? "Құжат №" : "№ документа",
      bank: kk ? "Банк" : "Банк",
      edit: kk ? "Өңдеу" : "Редактировать",
      del: kk ? "Жою" : "Удалить",
      active: kk ? "Белсенді" : "Активен",
      inactive: kk ? "Белсенді емес" : "Неактивен",
      loading: kk ? "Жүктелуде..." : "Загрузка...",
    };
  }, [lang]);

  const user = userDetails || baseUser;

  useEffect(() => {
    if (tab === "info" && typeof onInfoTabOpen === "function") {
      onInfoTabOpen();
    }
  }, [tab, onInfoTabOpen]);

  if (!baseUser && !userDetails) return null;

  const initials = (user?.name || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase())
    .join("");

  const statusRaw = String(user?.status || "").toLowerCase();
  const isActive =
    statusRaw === "белсенді" ||
    statusRaw === "активен" ||
    statusRaw === "active" ||
    statusRaw === "1";

  const handleOpenInfo = () => {
    setTab("info");
    if (typeof onInfoTabOpen === "function") onInfoTabOpen();
  };

  const handleOpenReviews = () => {
    setTab("reviews");
    if (typeof onReviewsTabOpen === "function") onReviewsTabOpen();
  };

  const rolesText =
    Array.isArray(user?.role_names) && user.role_names.length
      ? user.role_names.join(", ")
      : user?.role_name || user?.role?.name || "—";

  const deptText =
    user?.department?.name_kz ||
    user?.department?.name_ru ||
    user?.department?.name ||
    "—";

  const subjectText =
    user?.subject?.name_kz ||
    user?.subject?.name_ru ||
    user?.subject?.name ||
    "—";

  const statusText =
    lang === "kk"
      ? user?.status_label_kz || user?.status || "—"
      : user?.status_label_ru || user?.status || "—";

  const reviewCount = Number(reviewsResp?.summary?.rating_count || 0);

  return (
    <div className="up-wrap">
      <div className="up-hero">
        <div className="up-avatar">{initials || "U"}</div>

        <div className="up-hero-meta">
          <div className="up-name">{user?.name || "—"}</div>

          <div className="up-sub">
            <span>@{user?.username || "—"}</span>
            <span className="up-dot">•</span>
            <span>{user?.role_name || user?.role?.name || "—"}</span>
          </div>

          <div className="up-badges">
            <span className={"up-pill " + (isActive ? "is-ok" : "is-bad")}>
              {isActive ? t.active : t.inactive}
            </span>

            {subjectText !== "—" ? (
              <span className="up-pill is-ghost">{subjectText}</span>
            ) : null}

            {Array.isArray(user?.role_names) && user.role_names.length > 1 ? (
              <span className="up-pill is-ghost">{rolesText}</span>
            ) : null}
          </div>
        </div>

        {(canEdit || canDelete) && (
          <div className="up-actions">
            {canEdit && (
              <button
                type="button"
                className="up-btn"
                onClick={() => onEdit?.(user)}
              >
                {t.edit}
              </button>
            )}

            {canDelete && (
              <button
                type="button"
                className="up-btn up-btn-danger"
                onClick={() => onDelete?.(user)}
              >
                {t.del}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="up-tabs">
        <button
          type="button"
          className={"up-tab " + (tab === "info" ? "is-active" : "")}
          onClick={handleOpenInfo}
        >
          {t.info}
        </button>

        <button
          type="button"
          className={"up-tab " + (tab === "reviews" ? "is-active" : "")}
          onClick={handleOpenReviews}
        >
          {t.reviews}
          <span className="up-tab-badge">{reviewCount || ""}</span>
        </button>
      </div>

      {tab === "info" ? (
        infoLoading ? (
          <div className="up-loading">{t.loading}</div>
        ) : (
          <div className="up-grid">
            <div className="up-card">
              <div className="up-card-title">{t.contact}</div>
              <div className="up-kv">
                <div className="up-k">{t.phone}</div>
                <div className="up-v">{as(user?.phone)}</div>

                <div className="up-k">{t.address}</div>
                <div className="up-v">{as(user?.address)}</div>

                <div className="up-k">Email</div>
                <div className="up-v">{as(user?.email)}</div>
              </div>
            </div>

            <div className="up-card">
              <div className="up-card-title">{t.docs}</div>
              <div className="up-kv">
                <div className="up-k">{t.iin}</div>
                <div className="up-v">{as(user?.iin)}</div>

                <div className="up-k">{t.iik}</div>
                <div className="up-v">{as(user?.iik)}</div>

                <div className="up-k">{t.docNo}</div>
                <div className="up-v">{as(user?.document_number)}</div>

                <div className="up-k">{t.bank}</div>
                <div className="up-v">{as(user?.bank_name)}</div>
              </div>
            </div>

            <div className="up-card up-span">
              <div className="up-card-title">{t.work}</div>
              <div className="up-kv">
                <div className="up-k">{t.position}</div>
                <div className="up-v">{as(user?.position)}</div>

                <div className="up-k">{t.degree}</div>
                <div className="up-v">{as(user?.academic_degree)}</div>

                <div className="up-k">{t.dept}</div>
                <div className="up-v">{as(deptText)}</div>

                <div className="up-k">{t.subject}</div>
                <div className="up-v">{as(subjectText)}</div>

                <div className="up-k">{t.role}</div>
                <div className="up-v">{as(user?.role_name || user?.role?.name)}</div>

                <div className="up-k">{t.roles}</div>
                <div className="up-v">{as(rolesText)}</div>

                <div className="up-k">{t.status}</div>
                <div className="up-v">{as(statusText)}</div>

                <div className="up-k">{t.createdAt}</div>
                <div className="up-v">{as(user?.created_at)}</div>

                <div className="up-k">{t.updatedAt}</div>
                <div className="up-v">{as(user?.updated_at)}</div>
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="up-reviews">
          {reviewsLoading ? (
            <div className="up-loading">{t.loading}</div>
          ) : (
            <ReviewsModal
              locale={lang}
              expert={reviewsResp?.expert || user}
              summary={reviewsResp?.summary}
              reviewsPage={reviewsResp?.reviews}
              myReview={myReview}
              canWrite={canWriteReview}
              canManage={canManageReview}
              onPageChange={onReviewsPageChange}
              onSave={onReviewSave}
              onDeleteMine={onReviewDeleteMine}
              onToggleVisibility={onReviewToggleVisibility}
            />
          )}
        </div>
      )}
    </div>
  );
}